// Entry point creazione flow (Sprint 5).
// Tre opzioni: da template / duplica esistente / da zero.

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DogEarCard, RuleBicolor } from '@audiq/ui';
import { api, ApiClientError } from '../lib/api';

const TEMPLATES = [
  {
    slug: 'medico-base',
    name: 'Pre-triage medico generico',
    domain: 'Medico',
    description: 'Anamnesi pre-visita: motivo principale, durata, intensità, red flag emergenza.',
  },
  {
    slug: 'sociale-base',
    name: 'Sportello sociale',
    domain: 'Sociale',
    description: 'Accoglienza assistente sociale: bisogno espresso, urgenza, situazione abitativa.',
  },
  {
    slug: 'formativo-base',
    name: 'Iscrizione corso lingua',
    domain: 'Formativo',
    description: 'Pre-iscrizione scuola di lingua: livello attuale, obiettivo, disponibilità.',
  },
  {
    slug: 'legale-base',
    name: 'Primo contatto studio legale',
    domain: 'Legale',
    description: 'Inquadramento ambito (civile/penale/lavoro), urgenza, controparte.',
  },
];

type Choice = 'template' | 'duplicate' | 'scratch' | null;

export function FlowsNewEntryPage() {
  const navigate = useNavigate();
  const [choice, setChoice] = useState<Choice>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedExisting, setSelectedExisting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const flowsQuery = useQuery({
    queryKey: ['flows'],
    queryFn: () => api.listFlows({ per_page: 100 }),
    enabled: choice === 'duplicate',
  });

  const duplicateMut = useMutation({
    mutationFn: (id: string) => api.duplicateFlow(id),
    onSuccess: (flow) => {
      navigate(`/flows/${flow.id}?tab=persona`);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  if (choice === null) {
    return (
      <div className="mx-auto max-w-5xl">
        <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
          Configurazione · Nuovo flow
        </div>
        <h2 className="serif text-4xl" style={{ color: 'var(--ink)' }}>
          Da dove parti?
        </h2>
        <p className="mt-2 max-w-2xl" style={{ color: 'var(--mute)' }}>
          Tre modi per iniziare: template pronto, copia di un flow esistente, o foglio bianco.
        </p>
        <RuleBicolor className="my-6" />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ChoiceCard
            icon="📚"
            title="Da template"
            description="Parti da uno dei nostri template di esempio. Personalizzi tutto al passo successivo."
            onClick={() => setChoice('template')}
          />
          <ChoiceCard
            icon="📋"
            title="Duplica esistente"
            description="Clona un flow che hai già configurato. Mantieni le impostazioni, cambia solo cosa serve."
            onClick={() => setChoice('duplicate')}
          />
          <ChoiceCard
            icon="✨"
            title="Da zero"
            description="Inizia con un flow vuoto. Configurazione totalmente custom."
            onClick={() => setChoice('scratch')}
          />
        </div>
      </div>
    );
  }

  if (choice === 'scratch') {
    // Forwarda alla pagina classica
    navigate('/flows/new/scratch', { replace: true });
    return null;
  }

  if (choice === 'template') {
    return (
      <div className="mx-auto max-w-3xl">
        <button
          onClick={() => setChoice(null)}
          className="mb-3 text-sm underline"
          style={{ color: 'var(--mute)' }}
        >
          ← Cambia metodo
        </button>
        <h2 className="serif mb-2 text-3xl" style={{ color: 'var(--ink)' }}>
          Scegli un template
        </h2>
        <p className="mb-6" style={{ color: 'var(--mute)' }}>
          Ogni template è un punto di partenza. Tutto può essere modificato.
        </p>
        <div className="space-y-3">
          {TEMPLATES.map((t) => (
            <DogEarCard
              key={t.slug}
              className={`cursor-pointer rounded-md p-4 transition`}
              onClick={() => setSelectedTemplate(t.slug)}
              style={{
                borderColor: selectedTemplate === t.slug ? 'var(--primary)' : 'var(--line)',
                outline: selectedTemplate === t.slug ? '2px solid var(--primary)' : 'none',
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
                    {t.domain}
                  </div>
                  <div className="serif text-lg" style={{ color: 'var(--ink)' }}>
                    {t.name}
                  </div>
                  <div className="mt-1 text-sm" style={{ color: 'var(--mute)' }}>
                    {t.description}
                  </div>
                </div>
              </div>
            </DogEarCard>
          ))}
        </div>
        <div className="mt-6">
          <button
            disabled={!selectedTemplate}
            onClick={() => navigate(`/flows/new/scratch?template=${selectedTemplate}`)}
            className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            Continua con questo template →
          </button>
          <p className="mt-3 text-xs" style={{ color: 'var(--mute)' }}>
            Nota: i template di esempio sono già seedati nell'installazione. Lo Sprint 6 introdurrà
            l'import on-demand.
          </p>
        </div>
      </div>
    );
  }

  // duplicate
  return (
    <div className="mx-auto max-w-3xl">
      <button
        onClick={() => setChoice(null)}
        className="mb-3 text-sm underline"
        style={{ color: 'var(--mute)' }}
      >
        ← Cambia metodo
      </button>
      <h2 className="serif mb-2 text-3xl" style={{ color: 'var(--ink)' }}>
        Duplica un flow esistente
      </h2>
      <p className="mb-6" style={{ color: 'var(--mute)' }}>
        Cloniamo persona, boundaries, domande e outcomes. Il nuovo flow parte come bozza.
      </p>
      {flowsQuery.isLoading && (
        <p className="mono text-sm" style={{ color: 'var(--mute)' }}>
          Caricamento…
        </p>
      )}
      {flowsQuery.data && (
        <div className="space-y-2">
          {flowsQuery.data.flows.map((f) => (
            <button
              key={f.id}
              onClick={() => setSelectedExisting(f.id)}
              className="block w-full rounded-md border p-3 text-start transition"
              style={{
                background: 'var(--paper)',
                borderColor: selectedExisting === f.id ? 'var(--primary)' : 'var(--line)',
                outline: selectedExisting === f.id ? '2px solid var(--primary)' : 'none',
              }}
            >
              <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                {f.name}
              </div>
              <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
                {f.slug} · {f.is_active ? 'pubblicato' : 'bozza'}
              </div>
            </button>
          ))}
        </div>
      )}
      {error && (
        <p className="mt-3 text-sm" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
      <div className="mt-6">
        <button
          disabled={!selectedExisting || duplicateMut.isPending}
          onClick={() => selectedExisting && duplicateMut.mutate(selectedExisting)}
          className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          {duplicateMut.isPending ? 'Duplico…' : 'Duplica e apri →'}
        </button>
      </div>
    </div>
  );
}

function ChoiceCard({
  icon,
  title,
  description,
  onClick,
}: {
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <DogEarCard
      onClick={onClick}
      className="cursor-pointer rounded-md p-5 transition hover:shadow-md"
      style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
    >
      <div className="text-2xl">{icon}</div>
      <h3 className="serif mt-3 text-xl" style={{ color: 'var(--ink)' }}>
        {title}
      </h3>
      <p className="mt-1 text-sm" style={{ color: 'var(--mute)' }}>
        {description}
      </p>
    </DogEarCard>
  );
}
